import {
  CalendarOutlined,
  CloseOutlined,
  FileSearchOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Empty, Input, Modal, Spin } from "antd";
import { CREATE_jwel } from "../../../Config/Config";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const avatarBg = (name) => {
  const palette = [
    ["#1e3a5f", "#2d5282"],
    ["#14532d", "#15803d"],
    ["#7c2d12", "#c2410c"],
    ["#312e81", "#4338ca"],
    ["#701a75", "#a21caf"],
    ["#0c4a6e", "#0369a1"],
  ];
  const idx = (name || "A").charCodeAt(0) % palette.length;
  return palette[idx];
};

/**
 * Shared invoice/return/exchange selector dialog.
 *
 * apiTableName: DB table to query, e.g. "CREDIT_BILLMAST" (sale invoice, sale return, exchange bill)
 * extraWhere:   Extra SQL condition appended to the WHERE clause, e.g.
 *               "AND APPNO=0" for sale invoice, "" for sale return, "AND APPNO=1" for exchange bill
 * modifyExchangeDetAPI: optional callback, only needed by the exchange bill screen. Fired on
 *               Submit alongside the other modify*API callbacks when supplied.
 */
const EstimationSelectDialog = ({
  visibleHis,
  setVisibleHis,
  modifyOnlineAPI,
  modifyDetAPI,
  modifyLedgerAPI,
  modifyMastAPI,
  modifyExchangeDetAPI,
  setBillNo,
  setBillDate,
  handleReset,
  setSearchText,
  searchText,
  smCodeRef,
  apiTableName,
  extraWhere = "",
}) => {
  const searchInputRef = useRef(null);
  const submitBillNoRef = useRef(null);
  const [billData, setBillData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState([null, null]);
  const tenantName = localStorage.getItem("tenantName");
  const fromDate = dates[0] ? dates[0].format("YYYY-MM-DD") : "";
  const toDate = dates[1] ? dates[1].format("YYYY-MM-DD") : "";

  const filterDataByBillNo = (value) => {
    // Only reset if input is actually empty (not zero)
    if (value === "" || value === null || value === undefined) {
      handleReset();
      setBillNo();
      setBillDate();
      setSelectedRowKeys([]);
      setFilteredData(billData);
      smCodeRef.current?.focus();
      return;
    }

    const num = Number(value);
    const filtered = billData.filter((item) => item.billno === num);
    setFilteredData(filtered);

    // Auto-select exact match
    const exactMatch = billData.find((item) => item.billno === num);
    setBillDate(exactMatch?.billdate);
    setSelectedRowKeys(exactMatch ? [exactMatch.billno] : []);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    // Block typing zero
    if (value === "0") return;
    // Block zero as first digit (e.g., "01", "002")
    if (value.startsWith("0")) return;
    setSearchText(value);
    filterDataByBillNo(value);
  };

  const handleCheckboxChange = (billNo, billDate) => {
    const isSelected = selectedRowKeys.includes(billNo);
    const newKeys = isSelected ? [] : [billNo];
    const text = newKeys.join(", ");
    setSelectedRowKeys(newKeys);
    setSearchText(text);
    setBillNo(text);
    setBillDate(billDate);
    filterDataByBillNo(text);
  };

  const billNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=${apiTableName}&where=BILLDATE>='${fromDate}' AND BILLDATE<='${toDate}' ${extraWhere}&order=BILLNO DESC`,
        { headers: { tenantName: tenantName } },
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setBillData(data);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;
    setLoading(true);
    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=${apiTableName}&where=BILLDATE>='${fromDate}' AND BILLDATE<='${toDate}' ${extraWhere}&order=BILLNO DESC`,
        { headers: { tenantName } },
      )
      .then((res) => {
        setBillData(res.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dates, apiTableName, extraWhere]);

  useEffect(() => {
    const today = dayjs();
    setDates([today, today]);
  }, []);

  useEffect(() => {
    if (visibleHis) {
      setTimeout(() => searchInputRef.current?.focus(), 200);
    }
  }, [visibleHis]);

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  const displayData =
    filteredData.length > 0 || searchText ? filteredData : billData;

  const isDefaultDateRange =
    dates?.[0]?.isSame(dayjs(), "day") && dates?.[1]?.isSame(dayjs(), "day");
  const hasActiveFilter = searchText || !isDefaultDateRange;

  const handleClearFilters = () => {
    setSearchText("");
    setDates([dayjs(), dayjs()]);
    filterDataByBillNo();
  };

  const closeDialog = () => {
    setVisibleHis(false);
    handleReset();
    setSearchText();
    filterDataByBillNo();
    handleCheckboxChange();
    smCodeRef.current?.focus();
  };

  const submitSelected = () => {
    handleReset();
    modifyOnlineAPI(searchText);
    modifyDetAPI(searchText);
    modifyLedgerAPI(searchText);
    modifyMastAPI(searchText);
    if (modifyExchangeDetAPI) {
      modifyExchangeDetAPI(searchText);
    }
    setBillNo(searchText);
    setVisibleHis(false);
    smCodeRef.current?.focus();
  };

  return (
    <Modal
      open={visibleHis}
      onCancel={closeDialog}
      footer={null}
      closable={false}
      centered
      width={1080}
      styles={{
        body: { padding: 0 },
        content: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.20)",
          border: "1px solid #e2e8f0",
          padding: 0,
        },
        mask: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.20)",
        },
      }}
    >
      <style>{`
        @keyframes esd2-fadein { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes esd2-rowin { from { opacity:0; transform:translateX(5px); } to { opacity:1; transform:translateX(0); } }
        .esd2-body { animation: esd2-fadein 0.24s ease both; }

        .esd2-row {
          display: flex; align-items: center; gap: 12px; padding: 9px 14px; border-radius: 10px;
          cursor: pointer; border: 1.5px solid transparent; transition: all 0.15s ease;
          animation: esd2-rowin 0.18s ease both; background: #ffffff; margin-bottom: 6px;
        }
        .esd2-row:hover {
          border-color: #93c5fd;
          background: linear-gradient(135deg,#f0f7ff,#e8f2ff);
          transform: translateX(2px);
          box-shadow: 0 3px 12px rgba(59,130,246,0.12);
        }
        .esd2-row.selected {
          border-color: #3b82f6;
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          box-shadow: 0 3px 14px rgba(59,130,246,0.18);
        }
        .esd2-scroll::-webkit-scrollbar { width: 5px; }
        .esd2-scroll::-webkit-scrollbar-track { background: transparent; }
        .esd2-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .esd2-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .esd2-filter input { border-radius: 8px !important; font-size: 0.78rem !important; }
        .esd2-filter input:focus { border-color: #6b7280 !important; box-shadow: 0 0 0 2px rgba(107,114,128,0.15) !important; }
      `}</style>

      <div className="esd2-body">
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg,#1e3a5f 0%,#2d5282 100%)",
            padding: "16px 22px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {[
            { top: -26, right: -26, size: 80 },
            { bottom: -14, left: "35%", size: 50 },
          ].map((s, i) => (
            <div
              key={i}
              aria-hidden
              style={{
                position: "absolute",
                top: s.top,
                right: s.right,
                bottom: s.bottom,
                left: s.left,
                width: s.size,
                height: s.size,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.06)",
                pointerEvents: "none",
              }}
            />
          ))}

          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: "rgba(255,255,255,0.14)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FileSearchOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.3px",
              }}
            >
              Invoice Details
            </div>
            <div
              style={{
                fontSize: "0.68rem",
                color: "rgba(255,255,255,0.55)",
                marginTop: 2,
              }}
            >
              {loading
                ? "Loading invoices…"
                : `${displayData.length} of ${billData.length} invoices`}
            </div>
          </div>

          <Button
            type="text"
            onClick={billNoAPI}
            title="Refresh"
            style={{
              color: "rgba(255,255,255,0.70)",
              width: 32,
              height: 32,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              flexShrink: 0,
            }}
          >
            <ReloadOutlined style={{ fontSize: 13 }} />
          </Button>

          <Button
            type="text"
            onClick={closeDialog}
            style={{
              color: "rgba(255,255,255,0.70)",
              width: 32,
              height: 32,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              flexShrink: 0,
            }}
          >
            <CloseOutlined style={{ fontSize: 13 }} />
          </Button>
        </div>

        {/* Filter bar */}
        <div
          style={{
            background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
            padding: "11px 18px",
            display: "flex",
            gap: 10,
            alignItems: "center",
            borderBottom: "1px solid #e2e8f0",
            flexWrap: "wrap",
          }}
        >
          <RangePicker
            className="esd2-filter"
            value={dates}
            onChange={(values) => setDates(values || [dayjs(), dayjs()])}
            format="DD-MM-YYYY"
            allowClear={false}
            style={{ flex: 1.1, minWidth: 220, borderRadius: 8 }}
            maxDate={dayjs()}
            disabledDate={(current) =>
              current && current > dayjs().endOf("day")
            }
          />
          <Input
            className="esd2-filter"
            ref={searchInputRef}
            prefix={
              <SearchOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
            }
            placeholder="Search Inv No…"
            value={searchText}
            onChange={handleSearchInput}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => handleKeyDown(e, submitBillNoRef)}
            allowClear
            style={{ flex: 0.9, minWidth: 140 }}
          />
          {hasActiveFilter && (
            <Button
              type="text"
              onClick={handleClearFilters}
              style={{
                height: 32,
                padding: "0 10px",
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "#fff",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <CloseOutlined style={{ fontSize: 10 }} /> Clear
            </Button>
          )}
          <Button
            ref={submitBillNoRef}
            onClick={submitSelected}
            style={{
              height: 32,
              padding: "0 18px",
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#fff",
              background: "linear-gradient(135deg,#2d5282,#1e3a5f)",
              border: "none",
              borderRadius: "8px",
              flexShrink: 0,
            }}
          >
            Submit
          </Button>
        </div>

        {/* List */}
        <div
          className="esd2-scroll"
          style={{
            background: "linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)",
            padding: "12px 16px 14px",
            maxHeight: 440,
            overflowY: "auto",
          }}
        >
          <Spin spinning={loading} tip="Loading invoices…">
            {!loading && displayData.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ fontSize: "0.78rem", color: "#9ca3af" }}>
                    {billData.length === 0
                      ? "No invoices found"
                      : "No invoices match your filters"}
                  </span>
                }
                style={{ padding: "32px 0" }}
              >
                {hasActiveFilter && (
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    style={{ borderRadius: 7, fontWeight: 600 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Empty>
            )}

            {displayData.map((rec, idx) => {
              const isSelected = selectedRowKeys.includes(rec.billno);
              const [bg0, bg1] = avatarBg(rec.CustName);

              return (
                <div
                  key={`${rec.billno}-${idx}`}
                  className={`esd2-row${isSelected ? " selected" : ""}`}
                  style={{ animationDelay: `${Math.min(idx * 0.025, 0.4)}s` }}
                  onClick={() =>
                    handleCheckboxChange(rec.billno, rec?.billdate)
                  }
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: `linear-gradient(135deg,${bg0},${bg1})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 16,
                      flexShrink: 0,
                      boxShadow: `0 2px 8px ${bg1}55`,
                    }}
                  >
                    {(rec.CustName || "?")[0].toUpperCase()}
                  </div>

                  {/* Inv No badge */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      width: 74,
                      background: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
                      borderRadius: "8px",
                      padding: "4px 8px",
                      border: "1.5px solid #a5b4fc",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.52rem",
                        fontWeight: 700,
                        color: "#6366f1",
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                      }}
                    >
                      Inv No
                    </span>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: 800,
                        color: "#3730a3",
                        lineHeight: 1.1,
                        fontFamily: "monospace",
                      }}
                    >
                      #{rec.billno}
                    </span>
                  </div>

                  {/* Customer name + mobile */}
                  <div style={{ flex: 1.5, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "0.84rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rec.CustName || "—"}
                    </div>
                    <div
                      style={{
                        fontSize: "0.66rem",
                        color: "#6b7280",
                        marginTop: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontFamily: "monospace",
                      }}
                    >
                      <PhoneOutlined style={{ fontSize: 10 }} />
                      {rec.PhoneNo || "—"}
                    </div>
                  </div>

                  {/* Inv Date */}
                  <div
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 78,
                      background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
                      borderRadius: "7px",
                      padding: "4px 8px",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.55rem",
                        color: "#b45309",
                        fontWeight: 700,
                        letterSpacing: "0.3px",
                      }}
                    >
                      <CalendarOutlined
                        style={{ fontSize: 9, marginRight: 3 }}
                      />
                      INV DATE
                    </span>
                    <span
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#92400e",
                        fontFamily: "monospace",
                      }}
                    >
                      {rec.billdate
                        ? dayjs(rec.billdate).format("DD-MM-YYYY")
                        : "—"}
                    </span>
                  </div>

                  {/* Qty / Dis.Amt */}
                  <div style={{ flexShrink: 0, display: "flex", gap: 6 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 50,
                        background: "linear-gradient(135deg,#f8faff,#eef2ff)",
                        borderRadius: "7px",
                        padding: "4px 8px",
                        border: "1px solid #e0e7ff",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.55rem",
                          color: "#9ca3af",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                        }}
                      >
                        QTY
                      </span>
                      <span
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: "#374151",
                        }}
                      >
                        {rec?.totqty ?? rec?.TOTQTY ?? 0}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 60,
                        background: "linear-gradient(135deg,#f8faff,#eef2ff)",
                        borderRadius: "7px",
                        padding: "4px 8px",
                        border: "1px solid #e0e7ff",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.55rem",
                          color: "#9ca3af",
                          fontWeight: 700,
                          letterSpacing: "0.3px",
                        }}
                      >
                        DIS.AMT
                      </span>
                      <span
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: "#475569",
                          fontFamily: "monospace",
                        }}
                      >
                        {Number(rec.discount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Net Amt badge */}
                  <div
                    style={{ flexShrink: 0, minWidth: 90, textAlign: "right" }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: "8px",
                        background:
                          "linear-gradient(135deg,rgba(220,252,231,0.80),rgba(187,247,208,0.60))",
                        color: "#15803d",
                        fontFamily: "monospace",
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        border: "1px solid rgba(134,239,172,0.55)",
                      }}
                    >
                      ₹
                      {Number(rec.NETAMT || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 0,
                      })}
                    </span>
                  </div>

                  {/* Selection indicator */}
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isSelected
                        ? "rgba(59,130,246,0.18)"
                        : "rgba(100,116,139,0.08)",
                      border: isSelected
                        ? "1.5px solid rgba(59,130,246,0.40)"
                        : "1px solid rgba(100,116,139,0.20)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: isSelected ? "#1d4ed8" : "#94a3b8",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {isSelected ? "✓" : "›"}
                  </div>
                </div>
              );
            })}
          </Spin>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 20px",
            borderTop: "1px solid #eef0f4",
            background: "#f8fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: "0.68rem", color: "#9ca3af" }}>
            Select an invoice, then Submit to load it
          </span>
          <Button
            onClick={closeDialog}
            style={{
              height: 34,
              background: "#ffffff",
              border: "1.5px solid #d1d5db",
              color: "#6b7280",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.78rem",
            }}
          >
            <CloseOutlined style={{ fontSize: 11 }} /> Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EstimationSelectDialog;
